import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import apiClient from '../../services/api';

export interface DeepResearchRequest {
  contract_id: string;
  vendor_name: string;
  vendor_uei?: string;
  award_amount: number;
  award_date: string;
  awarding_agency: string;
  competition_type?: string;
  number_of_offers_received?: number;
  naics_code?: string;
  contract_description?: string;
  additional_data?: Record<string, any>;
}

export interface DeepResearchReport {
  id: string;
  status: 'pending' | 'completed' | 'error';
  created_at: string;
  completed_at?: string;
  contract_data?: DeepResearchRequest; // Optional for list responses
  defense_report?: string;
  prosecution_report?: string;
  judge_report?: string;
  risk_level?: 'CRITICAL' | 'HIGH' | 'MEDIUM' | 'LOW' | 'MINIMAL';
  fraud_risk_score?: number;
  error_message?: string;
  
  // Fields from list API response
  contract_id?: string;
  vendor_name?: string;
  award_amount?: number;
}

export interface DeepResearchState {
  reports: DeepResearchReport[];
  currentReport: DeepResearchReport | null;
  loading: boolean;
  pollingReportId: string | null;
  error: string | null;
}

const initialState: DeepResearchState = {
  reports: [],
  currentReport: null,
  loading: false,
  pollingReportId: null,
  error: null,
};

// Async thunks
export const createDeepResearchReport = createAsyncThunk(
  'deepResearch/createReport',
  async (request: DeepResearchRequest) => {
    const response = await apiClient.post('/deep-research/create', request);
    return response.data;
  }
);

export const getDeepResearchReport = createAsyncThunk(
  'deepResearch/getReport',
  async (reportId: string) => {
    const response = await apiClient.get(`/deep-research/${reportId}`);
    return response.data;
  }
);

export const listDeepResearchReports = createAsyncThunk(
  'deepResearch/listReports',
  async (params: { status?: string; limit?: number } = {}) => {
    const queryParams = new URLSearchParams();
    if (params.status) queryParams.append('status', params.status);
    if (params.limit) queryParams.append('limit', params.limit.toString());
    
    const response = await apiClient.get(`/deep-research/reports?${queryParams.toString()}`);
    return response.data;
  }
);

const deepResearchSlice = createSlice({
  name: 'deepResearch',
  initialState,
  reducers: {
    setCurrentReport: (state, action: PayloadAction<DeepResearchReport | null>) => {
      state.currentReport = action.payload;
    },
    setPollingReportId: (state, action: PayloadAction<string | null>) => {
      state.pollingReportId = action.payload;
    },
    clearError: (state) => {
      state.error = null;
    },
    updateReportInList: (state, action: PayloadAction<DeepResearchReport>) => {
      const index = state.reports.findIndex(r => r.id === action.payload.id);
      if (index !== -1) {
        state.reports[index] = action.payload;
      } else {
        state.reports.unshift(action.payload);
      }
    },
  },
  extraReducers: (builder) => {
    builder
      // Create report
      .addCase(createDeepResearchReport.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(createDeepResearchReport.fulfilled, (state, action) => {
        state.loading = false;
        const newReport = action.payload;
        state.reports.unshift(newReport);
        state.currentReport = newReport;
        if (newReport.status === 'pending') {
          state.pollingReportId = newReport.id;
        }
      })
      .addCase(createDeepResearchReport.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || 'Failed to create deep research report';
      })
      // Get report
      .addCase(getDeepResearchReport.pending, (state) => {
        // Don't set loading to true for polling requests to avoid UI flicker
      })
      .addCase(getDeepResearchReport.fulfilled, (state, action) => {
        const report = action.payload;
        
        // Update current report if it matches
        if (state.currentReport?.id === report.id) {
          state.currentReport = report;
        }
        
        // Update in reports list
        const index = state.reports.findIndex(r => r.id === report.id);
        if (index !== -1) {
          state.reports[index] = report;
        } else {
          state.reports.unshift(report);
        }
        
        // Stop polling if report is completed or error
        if (report.status === 'completed' || report.status === 'error') {
          state.pollingReportId = null;
        }
      })
      .addCase(getDeepResearchReport.rejected, (state, action) => {
        state.error = action.error.message || 'Failed to get deep research report';
        state.pollingReportId = null;
      })
      // List reports
      .addCase(listDeepResearchReports.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(listDeepResearchReports.fulfilled, (state, action) => {
        state.loading = false;
        state.reports = action.payload.reports || [];
      })
      .addCase(listDeepResearchReports.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || 'Failed to list deep research reports';
      });
  },
});

export const {
  setCurrentReport,
  setPollingReportId,
  clearError,
  updateReportInList,
} = deepResearchSlice.actions;

// Selectors
export const selectDeepResearchState = (state: { deepResearch: DeepResearchState }) => state.deepResearch;
export const selectDeepResearchReports = (state: { deepResearch: DeepResearchState }) => state.deepResearch.reports;
export const selectCurrentDeepResearchReport = (state: { deepResearch: DeepResearchState }) => state.deepResearch.currentReport;
export const selectDeepResearchLoading = (state: { deepResearch: DeepResearchState }) => state.deepResearch.loading;
export const selectDeepResearchError = (state: { deepResearch: DeepResearchState }) => state.deepResearch.error;
export const selectPollingReportId = (state: { deepResearch: DeepResearchState }) => state.deepResearch.pollingReportId;

export default deepResearchSlice.reducer;